import './styles.css'
import { lazy, Suspense, useMemo } from 'react'

import { HelmetProvider } from 'react-helmet-async'
import type { IntlShape } from 'react-intl/src/types.js'

import { RoleType } from '@tribeplatform/gql-client/types'
import { I18nProvider } from '@tribeplatform/react-components/i18n'
import {
  SocketProvider,
  ChatRoomSubscriptionsProvider,
  ChatMessagesProvider,
} from '@tribeplatform/react-components/Providers'
import {
  Provider,
  TrackerProvider,
  TribeEmitter,
  TribeEmitterEvent,
} from '@tribeplatform/react-sdk'
import { DocumentContext } from '@tribeplatform/react-ui-kit/hooks'

import { RuntimeConfigs } from '../../configs/runtime.js'
import { getAssetPath, StaticConfigs } from '../../configs/static.js'
import type { InjectedAppProps } from '../server/types/server.types.js'

import { Hydrate } from './components/Hydrate/Hydrate.js'
import { useRotateAccessToken } from './hooks/useRotateAccessToken.js'
import { useInteractions, interactionEmitter } from './interactions/index.js'
import { WebThemeProvider } from './new-themes/WebThemeProvider.js'
import { IntercomBootProvider } from './providers/IntercomBootProvider.js'
import { errorBoundaryHandler } from './utils/errors.utils.js'

const EmojiLoader = lazy(() =>
  import('./components/EmojiLoader.js').then(m => ({
    default: m.EmojiLoader,
  })),
)

interface Props {
  i18n: IntlShape
  injectedAppProps: InjectedAppProps
  children: React.ReactNode
}

export const App = ({ i18n, injectedAppProps, children }: Props) => {
  const {
    intercom,
    accessToken,
    colorScheme,
    authMember: {
      id: memberId,
      role: { id: memberRoleId, type: memberRoleType },
      locale,
    },
    network: {
      id: networkId,
      domain,
      domainSubfolder,
      organizationId,
      themes: oldTheme,
      activeTheme,
      settings: {
        privateMessaging: { privateMessagingEnabled },
      },
    },
  } = injectedAppProps
  const isGuest =
    !memberId || !memberRoleType || memberRoleType === RoleType.guest
  const isWebSocketEnabled = !!privateMessagingEnabled && !isGuest
  const rotateAccessToken = useRotateAccessToken({ domain, domainSubfolder })

  const documentContextValue = useMemo(
    () => ({
      document: typeof document !== 'undefined' ? document : undefined,
    }),
    [],
  )

  return (
    <DocumentContext.Provider value={documentContextValue}>
      <I18nProvider i18n={i18n}>
        <Provider
          config={{
            baseUrl: RuntimeConfigs.GQL_ENDPOINT,
            wsUrl: RuntimeConfigs.WS_ENDPOINT,
            networkDomain: domain,
            accessToken,
            giphyApiKey: RuntimeConfigs.GIPHY_API_KEY,
            unsplashApiKey: RuntimeConfigs.UNSPLASH_ACCESS_KEY,
            enableVectorSearch: RuntimeConfigs.VECTOR_SEARCH_ENABLED,
            enableAiSearch: RuntimeConfigs.AI_SEARCH_ENABLED,
            getAssetPath,
            interactionsContext: {
              useInteractions,
              interactionEmitter,
            },
            onAccessTokenExpired: rotateAccessToken,
            onEvent: event => {
              TribeEmitter.emit(TribeEmitterEvent.Event, {
                ...event,
                network: injectedAppProps.network,
              })
            },
            errorBoundary: {
              handler: errorBoundaryHandler,
            },
          }}
        >
          <TrackerProvider
            config={{
              memberId,
              memberRoleId,
              memberRoleType,
              networkId,
              memberLocale: locale,
              organizationId,
              enablePagePing: RuntimeConfigs.SNOWPLOW_ENABLE_PAGE_PING,
              enablePageView: RuntimeConfigs.SNOWPLOW_ENABLE_PAGE_VIEW,
              appVersion: StaticConfigs.APP_VERSION,
              releaseChannel: RuntimeConfigs.RELEASE_CHANNEL,
              snowplowCollectorAddress:
                RuntimeConfigs.SNOWPLOW_COLLECTOR_ADDRESS,
            }}
          >
            <HelmetProvider>
              <Hydrate injectedAppProps={injectedAppProps}>
                <IntercomBootProvider
                  enabled={intercom.enabled}
                  appId={intercom.appId}
                  bootProps={intercom.bootProps}
                >
                  <SocketProvider
                    networkId={injectedAppProps.network?.id}
                    config={{
                      endPoint: RuntimeConfigs.INBOX_WS_ENDPOINT,
                      path: RuntimeConfigs.INBOX_WS_PATH,
                      namespacePrefix: RuntimeConfigs.INBOX_WS_NAMESPACE_PREFIX,
                      enabled: isWebSocketEnabled,
                    }}
                    onSocketError={logger.error}
                    onIdentify={(err, res) => {
                      if (err) {
                        logger.error('Socket identify error', err)
                      } else {
                        logger.info('Socket identify response', res)
                      }
                    }}
                    onDebug={(event, payload) => {
                      if (RuntimeConfigs.INBOX_WS_DEBUG) {
                        logger.debug('WS Message', { event, payload })
                      }
                    }}
                  >
                    <ChatRoomSubscriptionsProvider
                      enabled={privateMessagingEnabled}
                    >
                      <ChatMessagesProvider>
                        <Suspense fallback={<></>}>
                          <EmojiLoader />
                        </Suspense>
                        <WebThemeProvider
                          oldColors={oldTheme?.active?.tokens?.colors}
                          networkThemeLightColors={activeTheme?.colors?.light}
                          networkThemeDarkColors={activeTheme?.colors?.dark}
                          networkThemeTypography={activeTheme?.typography}
                          colorScheme={colorScheme}
                          domain={domain}
                          domainSubfolder={domainSubfolder}
                        >
                          {children}
                        </WebThemeProvider>
                      </ChatMessagesProvider>
                    </ChatRoomSubscriptionsProvider>
                  </SocketProvider>
                </IntercomBootProvider>
              </Hydrate>
            </HelmetProvider>
          </TrackerProvider>
        </Provider>
      </I18nProvider>
    </DocumentContext.Provider>
  )
}
